/* This asset contains no private credentials and performs no stock redirection. */
(() => {
  'use strict';
  if (customElements.get('tgw-restock')) return;
  const nonce = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, '0')).join('');
  const uuid = () => crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => { const n = crypto.getRandomValues(new Uint8Array(1))[0] & 15; return (ch === 'x' ? n : (n & 3) | 8).toString(16); });
  const safeUrl = (url, service = false) => {
    try { const parsed = new URL(url); return parsed.protocol === 'https:' && (service ? parsed.hostname === 'script.google.com' : parsed.hostname === 'toothgemsworld.com') ? parsed.href : null; } catch { return null; }
  };
  class Restock extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.form = this.querySelector('[data-restock-form]');
      this.submit = this.querySelector('[data-restock-submit]');
      this.email = this.form.querySelector('[name="email"]');
      this.status = this.querySelector('[data-restock-status]');
      this.cancel = this.querySelector('[data-restock-cancel]');
      this.cancelLabel = this.cancel.textContent;
      this.label = this.submit.textContent;
      this.endpoint = safeUrl(this.dataset.endpoint, true);
      if (!this.endpoint || !this.form || !this.submit || !this.email) { this.hidden = true; return; }
      const options = { signal: this.controller.signal };
      this.form.addEventListener('submit', event => this.register(event), options);
      this.cancel.addEventListener('click', event => this.cancelAlert(event), options);
      const productForm = document.getElementById(this.dataset.formId);
      if (productForm) productForm.addEventListener('variant:change', event => this.updateVariant(event.detail.variant), options);
      this.updateVariant(JSON.parse(this.querySelector('[data-restock-variant]').textContent));
      this.form.addEventListener('focusin', () => { this.challenge().catch(() => {}); }, options);
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.challengePromise = null; this.challengeToken = null; }
    updateVariant(variant) {
      const previous = this.variant?.id;
      this.variant = variant && variant.id && variant.available === false ? variant : null;
      this.hidden = !this.variant;
      if (String(previous) !== String(this.variant?.id)) {
        this.requestId = uuid(); this.requestSecret = nonce(); this.status.textContent = ''; this.dataset.state = '';
        this.cancellation = null; this.cancel.hidden = true; this.cancel.disabled = false; this.cancel.textContent = this.cancelLabel;
        this.querySelector('[data-restock-purchase]').hidden = true;
        this.form.hidden = false;
      }
      this.submit.disabled = this.busy || !this.variant;
    }
    async challenge() {
      if (this.challengeToken && Date.now() - this.challengeAt < 500000) return this.challengeToken;
      if (this.challengePromise) return this.challengePromise;
      this.registrationNonce = nonce();
      this.challengePromise = this.rpc({ op: 'challenge', nonce: this.registrationNonce, origin: location.origin }, true).then(result => {
        if (!result.ok || !result.token) throw new Error('CHALLENGE');
        this.challengeAt = Date.now(); this.challengeToken = result.token; return result.token;
      }).finally(() => { this.challengePromise = null; });
      return this.challengePromise;
    }
    async register(event) {
      event.preventDefault();
      if (this.busy || !this.variant) return;
      if (!this.email.checkValidity()) { this.message(this.dataset.invalidEmail, 'error'); this.email.reportValidity(); return; }
      const variant = String(this.variant.id), requestId = this.requestId, requestSecret = this.requestSecret;
      const email = this.email.value.trim(), website = this.form.querySelector('[name="website"]').value;
      this.busy = true; this.setAttribute('data-busy', ''); this.submit.disabled = true;
      this.submit.textContent = this.dataset.loading; this.message('', 'loading');
      try {
        const token = await this.challenge();
        const delay = Math.max(0, 900 - (Date.now() - this.challengeAt));
        if (delay) await new Promise(resolve => setTimeout(resolve, delay));
        const result = await this.rpc({ op: 'subscribe', nonce: this.registrationNonce, token, requestId, requestSecret, productId: this.dataset.productId, variantId: variant, country: this.dataset.country, language: this.dataset.language.split('-')[0], email, website, origin: location.origin });
        if (!this.isConnected || String(this.variant?.id) !== variant || this.requestId !== requestId) return;
        if (!result.ok) { if (result.error === 'EXPIRED') this.challengeToken = null; throw new Error(result.error || 'FAILED'); }
        const purchase = this.querySelector('[data-restock-purchase]');
        const cancel = this.querySelector('[data-restock-cancel]');
        if (result.available || result.alreadyNotified) {
          const url = safeUrl(result.url); if (!url) throw new Error('URL');
          this.message(this.dataset.available, 'available'); purchase.href = url; purchase.hidden = false;
        } else {
          const url = safeUrl(result.cancelUrl, true); if (!(result.registered && result.duplicate) && (!result.id || !url)) throw new Error('CONFIRMATION');
          if (url) {
            const capability = new URL(url), endpoint = new URL(this.endpoint);
            const id = capability.searchParams.get('id'), token = capability.searchParams.get('token');
            if (capability.pathname !== endpoint.pathname || capability.searchParams.get('op') !== 'cancel' || id !== result.id || !token) throw new Error('CONFIRMATION');
            this.cancellation = { id, token, variant }; cancel.hidden = false;
          }
          this.message(this.dataset.success, 'success');
        }
        this.form.hidden = true;
      } catch {
        if (this.isConnected && String(this.variant?.id) === variant && this.requestId === requestId) this.message(this.dataset.error, 'error');
      } finally {
        this.busy = false; this.removeAttribute('data-busy'); this.submit.textContent = this.label; this.submit.disabled = !this.variant;
      }
    }
    async cancelAlert(event) {
      event.preventDefault();
      const capability = this.cancellation;
      if (this.cancelling || this.busy || !capability) return;
      this.cancelling = true; this.cancel.disabled = true; this.cancel.textContent = this.dataset.cancelling;
      this.message('', 'loading');
      try {
        const result = await this.rpc({ op: 'cancel', nonce: nonce(), origin: location.origin, id: capability.id, token: capability.token });
        if (!this.isConnected || this.cancellation !== capability) return;
        if (!result.ok || !(result.cancelled || result.alreadyNotified)) throw new Error('CANCELLATION');
        this.cancellation = null; this.cancel.hidden = true;
        this.message(result.alreadyNotified ? this.dataset.alreadyNotified : this.dataset.cancelled, 'cancelled');
      } catch {
        if (this.isConnected && this.cancellation === capability) this.message(this.dataset.cancelError, 'error');
      } finally {
        this.cancelling = false; this.cancel.disabled = false; this.cancel.textContent = this.cancelLabel;
      }
    }
    message(text, state) { this.status.textContent = text; this.dataset.state = state; }
    rpc(payload, get = false) {
      return new Promise((resolve, reject) => {
        const frame = document.createElement('iframe');
        frame.name = 'tgw-restock-' + nonce(); frame.hidden = true; frame.setAttribute('aria-hidden', 'true');
        let post;
        const finish = (error, value) => { clearTimeout(timer); window.removeEventListener('message', onMessage); this.controller?.signal.removeEventListener('abort', abort); frame.remove(); post?.remove(); error ? reject(error) : resolve(value); };
        const onMessage = event => {
          if (!/^https:\/\/(?:script\.google\.com|(?:[a-z0-9-]+-)?script\.googleusercontent\.com)$/.test(event.origin) || event.data?.channel !== 'tgw-restock' || event.data.op !== payload.op || event.data.nonce !== payload.nonce) return;
          finish(null, event.data.result);
        };
        const abort = () => finish(new Error('ABORTED'));
        const timer = setTimeout(() => finish(new Error('TIMEOUT')), 35000);
        window.addEventListener('message', onMessage);
        this.controller.signal.addEventListener('abort', abort, { once: true });
        document.body.append(frame);
        if (get) {
          const url = new URL(this.endpoint); Object.entries(payload).forEach(([key, value]) => url.searchParams.set(key, value)); frame.src = url.href;
        } else {
          post = document.createElement('form'); post.action = this.endpoint; post.method = 'post'; post.target = frame.name; post.hidden = true;
          Object.entries(payload).forEach(([name, value]) => { const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value; post.append(input); });
          document.body.append(post); post.submit();
        }
      });
    }
  }
  customElements.define('tgw-restock', Restock);
})();
